import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthProvider";
import ProtectedRoute from "./components/ProtectedRoute";

// Pages
import LoginPage from "./pages/LoginPage";
import StudentDashboard from "./pages/StudentDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import StartupProfile from "./pages/StartupProfile";
import FormBuilder from "./pages/FormBuilder";
import FormSubmission from "./pages/FormSubmission";
import Scoreboard from "./pages/Scoreboard";
import NotFoundPage from "./pages/NotFoundPage";
import AdminLayout from "./pages/AdminLayout";
import StartupsPage from "./pages/Startup/StartupListing";
import NewStartupPage from "./pages/Startup/CreateStartup";
import StartupDetailPage from "./pages/Startup/StartupDetail";
import CompetitionForm from "./pages/Startup/CompetitionForm";
import CompetitionListing from "./pages/Startup/CompetitionTable";
import CreateFormFromJSON from "./pages/CreateFormFromJSON";
import UsersListPage from "./pages/UserListPage";
import MyStartupPage from "./pages/Startup/MyStartup";
import AdminTeams from "./pages/admin/AdminTeamPage";
import StudentTeams from "./pages/student/StudentTeamsPage";
import StartupDashboard from "./pages/Startup/StartupDashboard";

// Learning Module Imports
import LearningPathsPage from "./pages/student/LearningPathsPage";
import LearningPathDetailPage from "./pages/student/LearningPathDetailPage";
import LearningProgressPage from "./pages/student/LearningProgressPage";
import LevelTestPage from "./pages/student/LevelTestPage";
import RankingPage from "./pages/student/RankingPage";
import AssignCreditsPage from "./pages/admin/AssignCreditsPage";
import CreditHistoryPage from "./pages/student/CreditHistoryPage";
import AdminLearningPathsPage from "./pages/admin/AdminLearningPathsPage";
import CreateLearningPathPage from "./pages/admin/CreateLearningPathPage";
import EditLearningPathPage from "./pages/admin/EditLearningPathPage";
import LearningPathScoreboardPage from "./pages/admin/LearningPathScoreboardPage";
import UserProfilePage from "./pages/student/UserProfilePage";

function App() {
    return (
        <Router>
            <AuthProvider>
                <Routes>
                    {/* ===== PUBLIC ROUTES ===== */}
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/startup-profile" element={<StartupProfile />} />

                    {/* ===== STUDENT ROUTES ===== */}
                    <Route
                        path="/student"
                        element={
                            <ProtectedRoute requiredRole="student">
                                <StudentDashboard />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/student/profile"
                        element={
                            <ProtectedRoute requiredRole={["student", "startup"]}>
                                <UserProfilePage />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/student/teams"
                        element={
                            <ProtectedRoute requiredRole="student">
                                <StudentTeams />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/student/learning/paths"
                        element={
                            <ProtectedRoute requiredRole="student">
                                <LearningPathsPage />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/student/learning/paths/:pathId"
                        element={
                            <ProtectedRoute requiredRole="student">
                                <LearningPathDetailPage />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/student/learning/progress"
                        element={
                            <ProtectedRoute requiredRole="student">
                                <LearningProgressPage />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/student/learning/test/:pathId/:levelId"
                        element={
                            <ProtectedRoute requiredRole="student">
                                <LevelTestPage />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/student/ranking"
                        element={
                            <ProtectedRoute requiredRole="student">
                                <RankingPage />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/student/credits/history"
                        element={
                            <ProtectedRoute requiredRole="student">
                                <CreditHistoryPage />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/student/startups"
                        element={
                            <ProtectedRoute requiredRole="student">
                                <StartupsPage />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/student/startup/new"
                        element={
                            <ProtectedRoute requiredRole="student">
                                <NewStartupPage />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/student/startup/:id"
                        element={
                            <ProtectedRoute requiredRole="student">
                                <StartupDetailPage />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/student/my-startup"
                        element={
                            <ProtectedRoute requiredRole="student">
                                <MyStartupPage />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/student/competitions"
                        element={
                            <ProtectedRoute requiredRole="student">
                                <CompetitionListing />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/student/competition/:id"
                        element={
                            <ProtectedRoute requiredRole="student">
                                <CompetitionForm />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/student/form/:formId"
                        element={
                            <ProtectedRoute requiredRole="student">
                                <FormSubmission />
                            </ProtectedRoute>
                        }
                    />

                    {/* ===== ADMIN ROUTES ===== */}
                    <Route
                        path="/admin"
                        element={
                            <ProtectedRoute requiredRole="admin">
                                <AdminDashboard />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/admin/users"
                        element={
                            <ProtectedRoute requiredRole="admin">
                                <UsersListPage />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/admin/teams"
                        element={
                            <ProtectedRoute requiredRole="admin">
                                <AdminTeams />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/admin/learning-paths"
                        element={
                            <ProtectedRoute requiredRole="admin">
                                <AdminLearningPathsPage />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/admin/learning-paths/create"
                        element={
                            <ProtectedRoute requiredRole="admin">
                                <CreateLearningPathPage />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/admin/learning-paths/:pathId/edit"
                        element={
                            <ProtectedRoute requiredRole="admin">
                                <EditLearningPathPage />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/admin/learning-paths/:pathId/scoreboard"
                        element={
                            <ProtectedRoute requiredRole="admin">
                                <LearningPathScoreboardPage />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/admin/form-builder/*"
                        element={
                            <ProtectedRoute requiredRole="admin">
                                <FormBuilder />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/admin/form/:formId"
                        element={
                            <ProtectedRoute requiredRole="admin">
                                <CreateFormFromJSON />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/admin/scoreboard"
                        element={
                            <ProtectedRoute requiredRole="admin">
                                <Scoreboard />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/admin/startups"
                        element={
                            <ProtectedRoute requiredRole="admin">
                                <StartupsPage />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/admin/competitions"
                        element={
                            <ProtectedRoute requiredRole="admin">
                                <CompetitionListing />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/admin/credits/assign"
                        element={
                            <ProtectedRoute requiredRole="admin">
                                <AssignCreditsPage />
                            </ProtectedRoute>
                        }
                    />

                    {/* ===== STARTUP ROUTES ===== */}
                    <Route
                        path="/startup"
                        element={
                            <ProtectedRoute requiredRole="startup">
                                <StartupDashboard />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/startup/profile"
                        element={
                            <ProtectedRoute requiredRole={["student", "startup"]}>
                                <UserProfilePage />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/startup/competitions"
                        element={
                            <ProtectedRoute requiredRole="startup">
                                <CompetitionListing />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/startup/competition/:id"
                        element={
                            <ProtectedRoute requiredRole="startup">
                                <CompetitionForm />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/startup/form/:formId"
                        element={
                            <ProtectedRoute requiredRole="startup">
                                <FormSubmission />
                            </ProtectedRoute>
                        }
                    />

                    {/* ===== CATCH-ALL ROUTES ===== */}
                    <Route path="/" element={<Navigate to="/student" replace />} />
                    <Route path="/404" element={<NotFoundPage />} />
                    <Route path="*" element={<NotFoundPage />} />
                </Routes>
            </AuthProvider>
        </Router>
    );
}

export default App;
